'use client';

import { createContext, type ReactNode, useContext } from 'react';

/**
 * OutletContext provides a way to pass children from Next.js layouts
 * to legacy components that use React Router's <Outlet /> pattern.
 *
 * Usage in Next.js layout:
 * ```tsx
 * <OutletProvider value={children}>
 *   <LegacyComponentWithOutlet />
 * </OutletProvider>
 * ```
 *
 * Usage in legacy component (replace Outlet import):
 * ```tsx
 * // Before: import { Outlet } from 'react-router-dom';
 * // After:
 * import { Outlet } from '@/providers/OutletProvider';
 * ```
 */

interface OutletContextValue {
  children: ReactNode;
  context?: unknown;
}

const OutletContext = createContext<OutletContextValue | null>(null);

interface OutletProviderProps {
  children: ReactNode;
  outlet: ReactNode;
  context?: unknown;
}

/**
 * OutletProvider wraps a legacy component and provides children
 * that will be rendered where <Outlet /> is used.
 *
 * @param children - The legacy component that contains <Outlet />
 * @param outlet - The content to render in place of <Outlet />
 * @param context - Optional context data (replaces useOutletContext)
 */
export function OutletProvider({ children, outlet, context }: OutletProviderProps) {
  return (
    <OutletContext.Provider value={{ children: outlet, context }}>
      {children}
    </OutletContext.Provider>
  );
}

/**
 * Outlet component - Drop-in replacement for react-router-dom's Outlet.
 * Renders the children provided by OutletProvider.
 *
 * If used outside of OutletProvider (e.g., still using react-router),
 * it returns null and lets the original Outlet handle rendering.
 *
 * When a context prop is passed (like in react-router), it wraps children
 * with a nested provider so useOutletContext() returns that context.
 */
export function Outlet({ context: propContext }: { context?: unknown }) {
  const outletContext = useContext(OutletContext);

  if (!outletContext) {
    // Not inside OutletProvider - this shouldn't happen in migrated routes
    // but provides a safe fallback
    // biome-ignore lint/suspicious/noConsole: Intentional warning for debugging migration issues
    console.warn('Outlet used outside of OutletProvider');
    return null;
  }

  // If a context prop was passed (react-router pattern), wrap children
  // with a nested provider so useOutletContext() returns that context
  if (propContext !== undefined) {
    return (
      <OutletContext.Provider value={{ children: outletContext.children, context: propContext }}>
        {outletContext.children}
      </OutletContext.Provider>
    );
  }

  return <>{outletContext.children}</>;
}

/**
 * useOutletContext - Drop-in replacement for react-router-dom's useOutletContext.
 * Returns the context value provided to OutletProvider.
 */
export function useOutletContext<T = unknown>(): T {
  const outletContext = useContext(OutletContext);

  if (!outletContext) {
    throw new Error('useOutletContext must be used within an OutletProvider');
  }

  return outletContext.context as T;
}

export default OutletProvider;
